import * as React from 'react';
import {
    Alert, Box, Breadcrumbs, Button, Card, CardContent, Chip, CircularProgress, Divider, FormControlLabel, Grid, Link as MuiLink,
    Stack, Switch, TextField, Tooltip, Typography,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
    AI_LABELS, clearAiTaskCache, createAiTask, deleteAiTask, executeAiTask, getAiTask, previewAiTask, updateAiTask,
} from '../../Api';
import {
    AI_DATA_CLASSES, AI_STATUSES, AiDataClass, AiJsonObject, AiParams, AiStatus, AiTaskDto, AiTaskExecutionDto,
    AiTaskPreviewDto, AiUpsertTaskRequest,
} from '../../models/dtos/AiDto';
import { ConfirmDialog } from '../../components/betupFeed/BetupFeedShared';
import { AiExportButton } from '../../components/ai/AiBundleExport';
import {
    AiActionError, AiEffectiveParams, AiCodeEditor, AiErrorAlert, AiModelPickers, aiLoadErrorText, EnumSelect, extractTemplateVariables,
    formatAiCount, formatAiDate, formatAiMs, formatAiUsd, JsonBlock, PARAMS_HELP, parseJsonText, parseOptionalNumber,
    StatLine, TextBlock, textError, toAiActionError, toJsonText, useAiCatalog,
} from '../../components/ai/AiShared';

interface TaskForm {
    code: string;
    description: string;
    ownerService: string;
    dataClass: AiDataClass;
    status: AiStatus;
    modelId: number | null;
    fallbackModelId: number | null;
    providerKeyId: number | null;
    maxOutputTokens: string;
    deadlineMs: string;
    systemPrompt: string;
    userTemplate: string;
    inputSchema: string;
    outputSchema: string;
    params: string;
    countsTowardUserBudgets: boolean;
    cacheEnabled: boolean;
    cacheTtlMinutes: string;
}

const EMPTY_FORM: TaskForm = {
    code: '', description: '', ownerService: '', dataClass: 'INTERNAL', status: 'ACTIVE', modelId: null,
    fallbackModelId: null, providerKeyId: null, maxOutputTokens: '1024', deadlineMs: '', systemPrompt: '',
    userTemplate: '', inputSchema: '', outputSchema: '', params: '',
    countsTowardUserBudgets: false, cacheEnabled: false, cacheTtlMinutes: '',
};

const CODE_PATTERN = /^[a-z0-9._-]+$/;
const MAX_CACHE_TTL_MINUTES = 43200;

function formFromTask(task: AiTaskDto): TaskForm {
    return {
        code: task.code,
        description: task.description ?? '',
        ownerService: task.ownerService ?? '',
        dataClass: task.dataClass,
        status: task.status,
        modelId: task.modelId,
        fallbackModelId: task.fallbackModelId,
        providerKeyId: task.providerKeyId,
        maxOutputTokens: task.maxOutputTokens != null ? String(task.maxOutputTokens) : '',
        deadlineMs: task.deadlineMs != null ? String(task.deadlineMs) : '',
        systemPrompt: task.systemPrompt ?? '',
        userTemplate: task.userTemplate ?? '',
        inputSchema: toJsonText(task.inputSchema),
        outputSchema: toJsonText(task.outputSchema),
        params: toJsonText(task.params),
        countsTowardUserBudgets: task.countsTowardUserBudgets ?? false,
        cacheEnabled: task.cacheEnabled ?? false,
        cacheTtlMinutes: task.cacheTtlMinutes != null ? String(task.cacheTtlMinutes) : '',
    };
}

function buildRequest(form: TaskForm): { request: AiUpsertTaskRequest | null; error: string | null } {
    if (!CODE_PATTERN.test(form.code)) return { request: null, error: 'Code is required: lowercase letters, digits, ".", "_", "-".' };
    const maxOutputTokens = parseOptionalNumber(form.maxOutputTokens);
    if (maxOutputTokens === undefined) return { request: null, error: 'Max output tokens must be a number.' };
    const deadlineMs = parseOptionalNumber(form.deadlineMs);
    if (deadlineMs === undefined) return { request: null, error: 'Deadline must be a number of milliseconds.' };
    const inputSchema = parseJsonText<AiJsonObject>(form.inputSchema, 'input schema');
    if (inputSchema.error) return { request: null, error: inputSchema.error };
    const outputSchema = parseJsonText<AiJsonObject>(form.outputSchema, 'output schema');
    if (outputSchema.error) return { request: null, error: outputSchema.error };
    const params = parseJsonText<AiParams>(form.params, 'params');
    if (params.error) return { request: null, error: params.error };
    // PERSONAL tasks cannot cache (the cache would hold personal data); the switch is off and disabled for them.
    const cacheEnabled = form.cacheEnabled && form.dataClass !== 'PERSONAL';
    const cacheTtlMinutes = parseOptionalNumber(form.cacheTtlMinutes);
    if (cacheTtlMinutes === undefined || (cacheTtlMinutes !== null && (cacheTtlMinutes < 1 || cacheTtlMinutes > MAX_CACHE_TTL_MINUTES))) {
        return { request: null, error: `Cache TTL must be between 1 and ${MAX_CACHE_TTL_MINUTES} minutes.` };
    }
    if (cacheEnabled && cacheTtlMinutes == null) return { request: null, error: 'Cache TTL (minutes) is required when the result cache is on.' };
    return {
        error: null,
        request: {
            code: form.code,
            description: form.description.trim() || null,
            ownerService: form.ownerService.trim() || null,
            dataClass: form.dataClass,
            systemPrompt: form.systemPrompt || null,
            userTemplate: form.userTemplate || null,
            inputSchema: inputSchema.value,
            outputSchema: outputSchema.value,
            modelId: form.modelId,
            fallbackModelId: form.fallbackModelId,
            providerKeyId: form.providerKeyId,
            maxOutputTokens,
            deadlineMs,
            params: params.value,
            status: form.status,
            countsTowardUserBudgets: form.countsTowardUserBudgets,
            cacheEnabled,
            // null keeps the saved TTL (it cannot be cleared, only changed).
            cacheTtlMinutes,
        },
    };
}

/** Per-user budget opt-in and the task result cache (with its stats and a clear button for a saved task). */
function TaskBudgetCacheCard({ form, task, patch, onCleared, onError }: {
    form: TaskForm;
    task: AiTaskDto | null;
    patch: (p: Partial<TaskForm>) => void;
    onCleared: (task: AiTaskDto | null, deleted: number) => void;
    onError: (error: AiActionError | null) => void;
}) {
    const [confirmClear, setConfirmClear] = React.useState(false);
    const [clearing, setClearing] = React.useState(false);
    const personal = form.dataClass === 'PERSONAL';
    const ttl = parseOptionalNumber(form.cacheTtlMinutes);
    const ttlInvalid = ttl === undefined || (ttl !== null && (ttl < 1 || ttl > MAX_CACHE_TTL_MINUTES)) || (form.cacheEnabled && !personal && ttl == null);
    const cache = task?.cache ?? null;

    const clear = async () => {
        if (task == null) return;
        setConfirmClear(false);
        setClearing(true);
        const result = await clearAiTaskCache(task.code);
        if (result.error != null) {
            setClearing(false);
            onError(toAiActionError(result, 'Clear cache failed'));
            return;
        }
        const reloaded = await getAiTask(task.code);
        setClearing(false);
        onCleared(reloaded, result.data?.deleted ?? 0);
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={5}>
                        <Typography variant="subtitle1" fontWeight={600}>Per-user budgets</Typography>
                        <FormControlLabel
                            control={<Switch checked={form.countsTowardUserBudgets} onChange={e => patch({ countsTowardUserBudgets: e.target.checked })} />}
                            label="Counts toward per-user budgets" />
                        <Typography variant="caption" color="text.secondary" display="block">
                            Only opted-in tasks are counted against and blocked by per-user budgets (AI › Budgets › Per user).
                            Calls without an end user are never affected.
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <Typography variant="subtitle1" fontWeight={600}>Result cache</Typography>
                        <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
                            <Tooltip title={personal ? 'PERSONAL tasks cannot cache results (the cache would hold personal data).' : ''}>
                                <span>
                                    <FormControlLabel disabled={personal}
                                        control={<Switch checked={form.cacheEnabled && !personal} onChange={e => patch({ cacheEnabled: e.target.checked })} />}
                                        label="Cache results" />
                                </span>
                            </Tooltip>
                            <TextField size="small" label="Cache TTL (minutes)" value={form.cacheTtlMinutes} sx={{ width: 200 }}
                                disabled={personal || !form.cacheEnabled} error={!personal && form.cacheEnabled && ttlInvalid}
                                helperText={`1–${MAX_CACHE_TTL_MINUTES.toLocaleString('en-US')} (30 days)`}
                                onChange={e => patch({ cacheTtlMinutes: e.target.value.replace(/[^\d]/g, '') })} />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" display="block">
                            {personal
                                ? 'Disabled: PERSONAL tasks cannot cache results.'
                                : 'Reuses a stored answer to the identical rendered prompt, model and params; a hit costs nothing. Tasks whose prompt contains changing input (dates, live lists) never hit the cache. Turning it off clears the stored answers.'}
                        </Typography>
                        {task != null && (
                            <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap" sx={{ mt: 1.5 }}>
                                {cache != null ? (
                                    <>
                                        <StatLine label="Entries" value={formatAiCount(cache.entries)} />
                                        <StatLine label="Live entries" value={formatAiCount(cache.liveEntries)} />
                                        <StatLine label="Hits" value={formatAiCount(cache.hits)} />
                                        <StatLine label="Last hit" value={formatAiDate(cache.lastHitAt)} />
                                    </>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">No cached answers yet.</Typography>
                                )}
                                <Button size="small" color="warning" variant="outlined" disabled={clearing}
                                    startIcon={clearing ? <CircularProgress size={14} /> : <DeleteSweepIcon />}
                                    onClick={() => setConfirmClear(true)}>
                                    Clear cache
                                </Button>
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
            <ConfirmDialog
                open={confirmClear}
                title="Clear the result cache?"
                text={<>All stored answers of task <b>{task?.code}</b> will be deleted; the next calls run the model again.</>}
                confirmLabel="Clear cache"
                confirmColor="warning"
                onClose={() => setConfirmClear(false)}
                onConfirm={clear}
            />
        </Card>
    );
}

function variablesTemplate(names: string[], previous: string): string {
    const parsed = parseJsonText<AiJsonObject>(previous, 'variables');
    const current: AiJsonObject = parsed.value ?? {};
    const next: AiJsonObject = {};
    names.forEach(n => { next[n] = n in current ? current[n] : ''; });
    Object.keys(current).forEach(k => { if (!(k in next)) next[k] = current[k]; });
    return JSON.stringify(next, null, 2);
}

/** Preview (render only) and test run (real model call, billed as caller betup-ai-admin) against the SAVED task. */
function TaskTestPanel({ code, variables, dirty }: { code: string; variables: string[]; dirty: boolean }) {
    const [variablesText, setVariablesText] = React.useState(() => variablesTemplate(variables, ''));
    const [imageUrl, setImageUrl] = React.useState('');
    const [busy, setBusy] = React.useState<'preview' | 'run' | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [preview, setPreview] = React.useState<AiTaskPreviewDto | null>(null);
    const [execution, setExecution] = React.useState<AiTaskExecutionDto | null>(null);

    const parseVariables = (): AiJsonObject | null => {
        const parsed = parseJsonText<AiJsonObject>(variablesText, 'variables');
        if (parsed.error) {
            setError(textError(parsed.error));
            return null;
        }
        return parsed.value ?? {};
    };

    const runPreview = async () => {
        const vars = parseVariables();
        if (vars == null) return;
        setBusy('preview');
        setError(null);
        const result = await previewAiTask(code, vars);
        setBusy(null);
        setError(toAiActionError(result, 'Preview failed'));
        setPreview(result.data);
    };

    const runExecution = async () => {
        const vars = parseVariables();
        if (vars == null) return;
        setBusy('run');
        setError(null);
        setExecution(null);
        const result = await executeAiTask(code, {
            variables: vars,
            attachments: imageUrl.trim() ? [{ type: 'image', url: imageUrl.trim() }] : undefined,
        });
        setBusy(null);
        setError(toAiActionError(result, 'Test run failed'));
        setExecution(result.data);
    };

    return (
        <Card variant="outlined">
            <CardContent>
                <Stack gap={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                        <Typography variant="h6">Preview & test run</Typography>
                        <Button size="small" onClick={() => setVariablesText(variablesTemplate(variables, variablesText))}>
                            Fill detected variables
                        </Button>
                    </Stack>
                    {dirty && <Alert severity="warning">You have unsaved changes — preview and test run use the saved version.</Alert>}
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <AiCodeEditor label="Variables (JSON object)" language="json" path={`ai-task-${code}-variables.json`}
                                value={variablesText} onChange={setVariablesText} height={200} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Stack gap={2}>
                                <TextField label="Image URL (optional attachment)" value={imageUrl} onChange={e => setImageUrl(e.target.value)} fullWidth
                                    helperText="Sent as an image attachment before the text (test run only)." />
                                <Stack direction="row" gap={1}>
                                    <Button variant="outlined" startIcon={busy === 'preview' ? <CircularProgress size={16} /> : <VisibilityIcon />}
                                        disabled={busy != null} onClick={runPreview}>Preview</Button>
                                    <Button variant="contained" startIcon={busy === 'run' ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                                        disabled={busy != null} onClick={runExecution}>Test run</Button>
                                </Stack>
                                <Typography variant="caption" color="text.secondary">
                                    Test run calls the model for real; it is recorded under caller <code>betup-ai-admin</code> and counts toward budgets.
                                </Typography>
                            </Stack>
                        </Grid>
                    </Grid>
                    <AiErrorAlert error={error} onClose={() => setError(null)} />
                    {preview && (
                        <Box>
                            <Divider sx={{ mb: 1.5 }} />
                            <Stack direction="row" gap={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap">
                                <Typography variant="subtitle1" fontWeight={600}>Rendered prompts</Typography>
                                {preview.missingVariables.length > 0
                                    ? <>
                                        <Typography variant="caption" color="error">Missing:</Typography>
                                        {preview.missingVariables.map(v => <Chip key={v} size="small" color="error" label={v} />)}
                                    </>
                                    : <Chip size="small" color="success" variant="outlined" label="no missing variables" />}
                            </Stack>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2">System prompt</Typography>
                                    <TextBlock text={preview.systemPrompt} maxHeight={320} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2">User prompt</Typography>
                                    <TextBlock text={preview.userPrompt} maxHeight={320} />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                    {execution && (
                        <Box>
                            <Divider sx={{ mb: 1.5 }} />
                            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Test run result</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <Stack gap={0.5}>
                                        <StatLine label="Execution" value={`#${execution.executionId}`} />
                                        <StatLine label="Model" value={execution.model} />
                                        <StatLine label="Provider key" value={execution.providerKey} />
                                        <StatLine label="Fallback used" value={execution.fallbackUsed ? 'yes' : 'no'} />
                                        <StatLine label="Input tokens" value={formatAiCount(execution.usage?.inputTokens)} />
                                        <StatLine label="Output tokens" value={formatAiCount(execution.usage?.outputTokens)} />
                                        <StatLine label="Cache read / write" value={`${formatAiCount(execution.usage?.cacheReadTokens)} / ${formatAiCount(execution.usage?.cacheWriteTokens)}`} />
                                        <StatLine label="Cost" value={formatAiUsd(execution.costUsd)} />
                                        <StatLine label="Latency" value={formatAiMs(execution.latencyMs)} />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    {execution.outputJson != null && (
                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="subtitle2">Output JSON</Typography>
                                            <JsonBlock value={execution.outputJson} />
                                        </Box>
                                    )}
                                    <Typography variant="subtitle2">Output text</Typography>
                                    <TextBlock text={execution.outputText} />
                                </Grid>
                            </Grid>
                        </Box>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function AiTaskEditPage() {
    const { code: codeParam } = useParams<{ code: string }>();
    const isNew = codeParam === 'new';
    const navigate = useNavigate();
    const catalog = useAiCatalog();
    const [task, setTask] = React.useState<AiTaskDto | null>(null);
    const [form, setForm] = React.useState<TaskForm>(EMPTY_FORM);
    const [saved, setSaved] = React.useState<TaskForm>(EMPTY_FORM);
    const [loading, setLoading] = React.useState(!isNew);
    const [loadError, setLoadError] = React.useState<string | null>(null);
    const [error, setError] = React.useState<AiActionError | null>(null);
    const [notice, setNotice] = React.useState<string | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [confirmDelete, setConfirmDelete] = React.useState(false);

    React.useEffect(() => {
        if (isNew || !codeParam) {
            setTask(null);
            setForm(EMPTY_FORM);
            setSaved(EMPTY_FORM);
            setLoading(false);
            return;
        }
        let cancelled = false;
        setLoading(true);
        getAiTask(codeParam).then(t => {
            if (cancelled) return;
            setLoading(false);
            if (t == null) {
                setLoadError(aiLoadErrorText(AI_LABELS.task, `Failed to load task ${codeParam}.`));
                return;
            }
            setLoadError(null);
            setTask(t);
            const f = formFromTask(t);
            setForm(f);
            setSaved(f);
        });
        return () => { cancelled = true; };
    }, [codeParam, isNew]);

    const patch = (p: Partial<TaskForm>) => setForm(f => ({ ...f, ...p }));
    const dirty = JSON.stringify(form) !== JSON.stringify(saved);
    const detected = extractTemplateVariables(form.systemPrompt, form.userTemplate);
    const serverVariables = task?.variables ?? [];
    const allVariables = detected.concat(serverVariables.filter(v => !detected.includes(v)));

    const save = async () => {
        const { request, error: validation } = buildRequest(form);
        if (request == null) {
            setError(textError(validation ?? 'Invalid form'));
            return;
        }
        setSaving(true);
        setError(null);
        setNotice(null);
        const result = isNew ? await createAiTask(request) : await updateAiTask(codeParam ?? request.code, request);
        setSaving(false);
        if (result.error != null || result.data == null) {
            setError(toAiActionError(result, 'Save failed') ?? textError('Save failed: empty response'));
            return;
        }
        const f = formFromTask(result.data);
        setTask(result.data);
        setForm(f);
        setSaved(f);
        setNotice(isNew ? `Task ${result.data.code} created.` : 'Saved.');
        if (isNew) navigate(`/ai/tasks/${encodeURIComponent(result.data.code)}`, { replace: true });
    };

    const remove = async () => {
        if (!codeParam) return;
        setConfirmDelete(false);
        setSaving(true);
        const result = await deleteAiTask(codeParam);
        setSaving(false);
        if (result.error != null) {
            setError(toAiActionError(result, 'Delete failed'));
            return;
        }
        navigate('/ai/tasks');
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
    }

    return (
        <Stack gap={2} sx={{ mx: 5, my: 4 }}>
            <Breadcrumbs>
                <MuiLink component={RouterLink} to="/ai" underline="hover">AI</MuiLink>
                <MuiLink component={RouterLink} to="/ai/tasks" underline="hover">Tasks</MuiLink>
                <Typography color="text.primary">{isNew ? 'New task' : codeParam}</Typography>
            </Breadcrumbs>
            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Box>
                    <Stack direction="row" gap={1} alignItems="center">
                        <Typography variant="h4" sx={{ fontFamily: isNew ? undefined : 'monospace' }}>{isNew ? 'New task' : codeParam}</Typography>
                        {(task?.workflows ?? (task?.workflowCode ? [{ code: task.workflowCode, primary: true }] : [])).map(w => (
                            <Chip key={w.code} size="small" variant={w.primary ? 'filled' : 'outlined'} clickable component={RouterLink}
                                label={`workflow ${w.code}${w.primary ? ' (primary)' : ''}`}
                                to={`/ai/workflows/${encodeURIComponent(w.code)}`} />
                        ))}
                    </Stack>
                    {task && (
                        <Typography variant="caption" color="text.secondary">
                            Updated {formatAiDate(task.updatedAt)}{task.updatedBy ? ` by ${task.updatedBy}` : ''} · created {formatAiDate(task.createdAt)}
                        </Typography>
                    )}
                </Box>
                <Stack direction="row" gap={1} alignItems="center">
                    {dirty && <Chip size="small" color="warning" label="unsaved changes" />}
                    {!isNew && codeParam && (
                        <Button component={RouterLink} to={`/ai/task-executions?taskCode=${encodeURIComponent(codeParam)}`}>Executions</Button>
                    )}
                    {!isNew && codeParam && (
                        <AiExportButton request={{ tasks: [codeParam] }} disabled={saving || loadError != null}
                            tooltip={dirty ? 'Downloads the saved version as a bundle file — save first to include your changes.' : 'Downloads this task as a bundle file.'}
                            onOutcome={o => { setError(o.error); setNotice(o.notice); }} />
                    )}
                    {!isNew && (
                        <Button color="error" variant="outlined" startIcon={<DeleteIcon />} disabled={saving} onClick={() => setConfirmDelete(true)}>
                            Delete
                        </Button>
                    )}
                    <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        disabled={saving || (!dirty && !isNew) || loadError != null} onClick={save}>
                        {isNew ? 'Create' : 'Save'}
                    </Button>
                </Stack>
            </Stack>
            {loadError && <Alert severity="error">{loadError}</Alert>}
            {catalog.error && <Alert severity="warning">{catalog.error}</Alert>}
            <AiErrorAlert error={error} onClose={() => setError(null)} />
            {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}

            {loadError == null && (
                <>
                    <Card variant="outlined">
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={4}>
                                    <TextField label="Code" fullWidth required value={form.code} disabled={!isNew}
                                        onChange={e => patch({ code: e.target.value.toLowerCase() })}
                                        error={form.code !== '' && !CODE_PATTERN.test(form.code)}
                                        helperText={isNew ? 'lowercase letters, digits, ".", "_", "-" (e.g. moderation.feed-post)' : 'Code cannot be changed'} />
                                </Grid>
                                <Grid item xs={12} md={5}>
                                    <TextField label="Description" fullWidth value={form.description} onChange={e => patch({ description: e.target.value })} />
                                </Grid>
                                <Grid item xs={12} md={3}>
                                    <TextField label="Owner service" fullWidth value={form.ownerService} onChange={e => patch({ ownerService: e.target.value })}
                                        placeholder="betup-server-api" />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <EnumSelect label="Data class" value={form.dataClass} options={catalog.meta?.dataClasses ?? AI_DATA_CLASSES}
                                        onChange={v => v && patch(v === 'PERSONAL' ? { dataClass: v, cacheEnabled: false } : { dataClass: v })} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <EnumSelect label="Status" value={form.status} options={catalog.meta?.statuses ?? AI_STATUSES}
                                        onChange={v => v && patch({ status: v })} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField label="Max output tokens" fullWidth value={form.maxOutputTokens}
                                        onChange={e => patch({ maxOutputTokens: e.target.value.replace(/[^\d]/g, '') })} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField label="Deadline (ms)" fullWidth value={form.deadlineMs} placeholder="default"
                                        onChange={e => patch({ deadlineMs: e.target.value.replace(/[^\d]/g, '') })} />
                                </Grid>
                                <Grid item xs={12} md={4} />
                                <Grid item xs={12} md={4}>
                                    <Stack gap={2}>
                                        <AiModelPickers catalog={catalog} modelId={form.modelId} fallbackModelId={form.fallbackModelId}
                                            providerKeyId={form.providerKeyId} onChange={p => patch(p)} />
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <Stack gap={2}>
                                        <AiCodeEditor label="Params (JSON)" language="json" path={`ai-task-${codeParam}-params.json`}
                                            value={form.params} onChange={v => patch({ params: v })} height={190} helper={PARAMS_HELP} />
                                        <AiEffectiveParams modelId={form.modelId} fallbackModelId={form.fallbackModelId} paramsText={form.params} />
                                    </Stack>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <TaskBudgetCacheCard form={form} task={task} patch={patch} onError={setError}
                        onCleared={(reloaded, deleted) => {
                            // Only the read-only cache stats change; keep the form (and any unsaved edits).
                            if (reloaded != null) setTask(reloaded);
                            setError(null);
                            setNotice(`Cache cleared: ${deleted} ${deleted === 1 ? 'entry' : 'entries'} deleted.`);
                        }} />

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <AiCodeEditor label="System prompt" language="plaintext" path={`ai-task-${codeParam}-system.txt`}
                                value={form.systemPrompt} onChange={v => patch({ systemPrompt: v })} height={360} />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <AiCodeEditor label="User template" language="plaintext" path={`ai-task-${codeParam}-user.txt`}
                                value={form.userTemplate} onChange={v => patch({ userTemplate: v })} height={360}
                                helper="{{name}} placeholders are replaced by variables: strings as-is, numbers/booleans as text, objects/arrays as pretty JSON, null as empty." />
                        </Grid>
                    </Grid>
                    <Stack direction="row" gap={0.75} alignItems="center" flexWrap="wrap">
                        <Typography variant="subtitle2" sx={{ mr: 0.5 }}>Variables:</Typography>
                        {allVariables.length === 0 && <Typography variant="body2" color="text.secondary">none</Typography>}
                        {allVariables.map(v => {
                            const inEditor = detected.includes(v);
                            const onServer = serverVariables.includes(v);
                            const title = inEditor && onServer ? 'In the saved task and the editor'
                                : inEditor ? 'Only in the editor (unsaved)' : 'Only in the saved task (removed in the editor)';
                            return (
                                <Tooltip key={v} title={title}>
                                    <Chip size="small" label={`{{${v}}}`} sx={{ fontFamily: 'monospace' }}
                                        color={inEditor && onServer ? 'primary' : inEditor ? 'warning' : 'default'}
                                        variant={inEditor ? 'filled' : 'outlined'} />
                                </Tooltip>
                            );
                        })}
                    </Stack>
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <AiCodeEditor label="Input schema (JSON Schema, optional)" language="json" path={`ai-task-${codeParam}-input-schema.json`}
                                value={form.inputSchema} onChange={v => patch({ inputSchema: v })} height={260}
                                helper="Validates the variables object before rendering." />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <AiCodeEditor label="Output schema (JSON Schema, optional)" language="json" path={`ai-task-${codeParam}-output-schema.json`}
                                value={form.outputSchema} onChange={v => patch({ outputSchema: v })} height={260}
                                helper="When set, output must be valid JSON matching it (one repair attempt, else 422 OUTPUT_INVALID)." />
                        </Grid>
                    </Grid>

                    {isNew
                        ? <Alert severity="info">Create the task to preview and test it.</Alert>
                        : codeParam && <TaskTestPanel code={codeParam} variables={allVariables} dirty={dirty} />}
                </>
            )}

            <ConfirmDialog
                open={confirmDelete}
                title="Delete task?"
                text={<>Task <b>{codeParam}</b> will be deleted. Services calling it will get 404 NOT_FOUND. Consider disabling it instead.</>}
                confirmLabel="Delete"
                confirmColor="error"
                onClose={() => setConfirmDelete(false)}
                onConfirm={remove}
            />
        </Stack>
    );
}
